import { memo, useCallback, useState, useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { Minus, TriangleAlert } from 'lucide-react'
import { useStore, usePrograms, useProgramsLoading, useProgramQuery, useSelectedProgram, useHasPermission } from '../../store/index.js'
import { useProgramList } from '../../hooks/usePrograms.js'
import { Button } from '../ui/button.js'
import { Checkbox } from '../ui/checkbox.js'
import type { Program } from '../../types/index.js'
import type { Collection, Series, ProgramCategory, ProgramGenre } from '../../types/domain.js'
import { collectionsApi, programsApi, seriesApi, programCategoriesApi, programGenresApi } from '../../lib/api.js'
import { PROGRAM_TYPE_LABELS, ANTENNA_ELEMENT_PROGRAM_TYPES, formatDurationShort } from '../../lib/utils.js'
import { updateRangeSelection } from '../../lib/range-selection.js'
import { ProgramBulkActions } from './ProgramBulkActions.js'
import type { ActiveCatalogFilter } from './ProgramsView.js'
import { Spinner } from '../common/Spinner.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ProgramListProps {
  channelId:        string | null
  onSelectProgram:  (p: Program) => void
  onProgramsDeleted: (ids: string[]) => void
  activeFilter?:    ActiveCatalogFilter | null
  onClearFilter?:   () => void
  /** Mode navigation seule pendant l'édition : masque la barre de filtres. */
  compact?:         boolean
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const ProgramList = memo(function ProgramList({ channelId, onSelectProgram, onProgramsDeleted, activeFilter, onClearFilter, compact = false }: ProgramListProps) {
  const { total, hasMore, loadingMore, loadMore, reload } = useProgramList(channelId)
  const allPrograms     = usePrograms()
  // Exclure les jingles et coming-next du catalogue de programmes
  const programs        = useMemo(() => allPrograms.filter(p => !ANTENNA_ELEMENT_PROGRAM_TYPES.includes(p.type)), [allPrograms])
  const programIds      = useMemo(() => programs.map(p => p.id), [programs])
  const loading         = useProgramsLoading()
  const query           = useProgramQuery()
  const selected        = useSelectedProgram()
  const canDelete       = useHasPermission('PROGRAM_DELETE')
  const setProgramQuery = useStore(state => state.setProgramQuery)
  const setSelectedProgram = useStore(state => state.setSelectedProgram)

  // Listes dynamiques pour les sélecteurs de collections et séries
  const [collectionsList, setCollectionsList] = useState<Collection[]>([])
  const [seriesList, setSeriesList]           = useState<Series[]>([])
  const [categoriesList, setCategoriesList]   = useState<ProgramCategory[]>([])
  const [genresList,     setGenresList]       = useState<ProgramGenre[]>([])
  const [showFilterMenu, setShowFilterMenu]   = useState(false)
  const [selectedIds, setSelectedIds]         = useState<Set<string>>(() => new Set())
  const [deleting, setDeleting]               = useState(false)

  const filterMenuRef = useRef<HTMLDivElement>(null)
  const scrollRef     = useRef<HTMLDivElement>(null)
  const sentinelRef   = useRef<HTMLDivElement>(null)
  const selectionAnchorRef = useRef<string | null>(null)
  const [sentinelVisible, setSentinelVisible] = useState(false)

  useEffect(() => {
    if (!channelId) {
      setCollectionsList([])
      setSeriesList([])
      setCategoriesList([])
      setGenresList([])
      return
    }
    collectionsApi.list({ channelId }).then(setCollectionsList).catch(() => setCollectionsList([]))
    seriesApi.list({ channelId }).then(setSeriesList).catch(() => setSeriesList([]))
    programCategoriesApi.list({ channelId }).then(setCategoriesList).catch(() => setCategoriesList([]))
    programGenresApi.list({ channelId }).then(setGenresList).catch(() => setGenresList([]))
  }, [channelId])

  useEffect(() => {
    const visibleIds = new Set(programIds)
    setSelectedIds(previous => {
      const next = new Set([...previous].filter(id => visibleIds.has(id)))
      return next.size === previous.size ? previous : next
    })
    if (selectionAnchorRef.current && !visibleIds.has(selectionAnchorRef.current)) {
      selectionAnchorRef.current = null
    }
  }, [programIds])

  // Fermeture du menu filtre lors d'un clic extérieur
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target as Node)) {
        setShowFilterMenu(false)
      }
    }
    if (showFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showFilterMenu])

  // ── Scroll infini ────────────────────────────────────────────────────────
  // On suit la visibilité du pied de liste plutôt que de déclencher au premier
  // franchissement : une page entièrement composée de jingles (filtrés côté
  // client) n'allonge pas la liste, il faut donc pouvoir enchaîner les pages
  // sans nouveau geste de l'opérateur.
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore) {
      setSentinelVisible(false)
      return
    }
    const observer = new IntersectionObserver(
      entries => setSentinelVisible(entries[0]?.isIntersecting ?? false),
      { root: scrollRef.current, rootMargin: '200px' },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore])

  useEffect(() => {
    if (sentinelVisible && hasMore && !loading && !loadingMore) void loadMore()
  }, [sentinelVisible, hasMore, loading, loadingMore, loadMore])

  const handleSearch = useCallback((v: string) => {
    setProgramQuery({ search: v || undefined })
  }, [setProgramQuery])

  const handleFilter = useCallback((key: 'all' | 'category' | 'isLive' | 'archived', value?: string) => {
    if (key === 'all')           setProgramQuery({ programCategoryId: undefined, programGenreId: undefined, isLive: undefined, search: undefined, collectionId: undefined, seriesId: undefined, archived: undefined })
    else if (key === 'category') setProgramQuery({ programCategoryId: value, isLive: undefined, archived: undefined })
    else if (key === 'isLive')   setProgramQuery({ isLive: 'true', programCategoryId: undefined, archived: undefined })
    else if (key === 'archived') setProgramQuery({ programCategoryId: undefined, isLive: undefined, collectionId: undefined, seriesId: undefined, archived: 'archived' })
  }, [setProgramQuery])

  const hasActiveCategoryFilters = !!(query.programCategoryId || query.programGenreId || query.isLive || query.archived || query.collectionId || query.seriesId)
  const hasActiveFilters = hasActiveCategoryFilters || !!query.search

  const handleClearAllFilters = useCallback(() => {
    setProgramQuery({
      programCategoryId: undefined,
      programGenreId: undefined,
      isLive: undefined,
      search: undefined,
      collectionId: undefined,
      seriesId: undefined,
      archived: undefined,
    })
    if (onClearFilter) onClearFilter()
  }, [setProgramQuery, onClearFilter])

  const isFiltered = !!activeFilter
  const filterStyle = activeFilter?.color
    ? { '--catalog-accent': activeFilter.color } as CSSProperties
    : undefined

  const handleSelect = useCallback((p: Program) => {
    setSelectedProgram(p.id)
    onSelectProgram(p)
  }, [setSelectedProgram, onSelectProgram])

  const handleCheck = useCallback((p: Program, checked: boolean, shiftKey: boolean) => {
    setSelectedIds(previous => shiftKey
      ? updateRangeSelection(programIds, previous, selectionAnchorRef.current, p.id, checked)
      : (() => {
          const next = new Set(previous)
          if (checked) next.add(p.id)
          else next.delete(p.id)
          return next
        })())
    if (!shiftKey) selectionAnchorRef.current = p.id
  }, [programIds])

  const allSelected = programIds.length > 0 && programIds.every(id => selectedIds.has(id))
  const someSelected = programIds.some(id => selectedIds.has(id))

  const handleCheckAll = useCallback(() => {
    setSelectedIds(allSelected ? new Set() : new Set(programIds))
    selectionAnchorRef.current = null
  }, [allSelected, programIds])

  const handleDeleteSelected = useCallback(async () => {
    const selectedPrograms = programs.filter(program => selectedIds.has(program.id))
    if (!canDelete || selectedPrograms.length === 0) return
    if (!confirm(`Supprimer définitivement ${selectedPrograms.length} programme${selectedPrograms.length > 1 ? 's' : ''} du catalogue ?\n\nLes fichiers médias resteront sur le disque.`)) return

    setDeleting(true)
    try {
      const results = await Promise.allSettled(
        selectedPrograms.map(program => programsApi.deleteFromCatalog(program.id, program.active)),
      )
      const deletedIds = selectedPrograms
        .filter((_, index) => results[index]?.status === 'fulfilled')
        .map(program => program.id)

      if (deletedIds.length > 0) {
        const deletedSet = new Set(deletedIds)
        setSelectedIds(previous => new Set([...previous].filter(id => !deletedSet.has(id))))
        onProgramsDeleted(deletedIds)
        await reload()
      }
      selectionAnchorRef.current = null

      const failed = selectedPrograms.length - deletedIds.length
      if (failed > 0) {
        alert(`${failed} programme${failed > 1 ? 's' : ''} n'${failed > 1 ? 'ont' : 'a'} pas pu être supprimé${failed > 1 ? 's' : ''}. ${failed > 1 ? 'Ils sont' : 'Il est'} peut-être encore référencé${failed > 1 ? 's' : ''} dans une grille, une récurrence ou un modèle.`)
      }
    } finally {
      setDeleting(false)
    }
  }, [canDelete, onProgramsDeleted, programs, reload, selectedIds])

  return (
    <div className="catalog-list">

      {/* ── Toolbar avec champ de recherche pleine largeur & filtre popover ─── */}
      {!compact && (
      <div className="catalog-toolbar" ref={filterMenuRef}>
        <div className="catalog-search-wrapper">
          <button
            type="button"
            className={`catalog-filter-toggle-btn${hasActiveCategoryFilters ? ' is-active' : ''}`}
            onClick={() => setShowFilterMenu(prev => !prev)}
            title="Règles de filtrage et tri"
            aria-label="Ouvrir les filtres et règles de filtrage"
            aria-expanded={showFilterMenu}
          >
            <svg className="catalog-filter-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            {hasActiveCategoryFilters && <span className="catalog-filter-badge-dot" />}
          </button>

          <input
            type="text"
            placeholder="Rechercher un programme…"
            value={query.search ?? ''}
            onChange={e => handleSearch(e.target.value)}
            className="tp-input catalog-search catalog-search-with-filter-btn"
            aria-label="Rechercher un programme"
          />

          {showFilterMenu && (
            <div className="catalog-filter-popover" role="dialog" aria-label="Règles de filtrage">
              <div className="catalog-filter-popover__header">
                <span>Règles de filtrage & tri</span>
                <button type="button" onClick={() => setShowFilterMenu(false)} className="catalog-filter-popover__close" aria-label="Fermer">×</button>
              </div>

              {/* Format */}
              <div className="catalog-filter-popover__group">
                <label className="catalog-filter-popover__label">Format</label>
                <select
                  className="tp-select catalog-filter-select"
                  value={
                    query.archived === 'archived' ? 'archived' :
                    query.isLive === 'true' ? 'isLive' :
                    query.programCategoryId ?? 'all'
                  }
                  onChange={e => {
                    const val = e.target.value
                    if (val === 'all') handleFilter('all')
                    else if (val === 'isLive') handleFilter('isLive')
                    else if (val === 'archived') handleFilter('archived')
                    else handleFilter('category', val)
                  }}
                  aria-label="Filtrer par format"
                >
                  <option value="all">Tous formats</option>
                  {categoriesList.map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                  <option value="isLive">Directs</option>
                  <option value="archived">Archivés</option>
                </select>
              </div>

              {/* Genre — l'axe éditorial publié à l'EPG, distinct du format. */}
              <div className="catalog-filter-popover__group">
                <label className="catalog-filter-popover__label">Genre</label>
                <select
                  className="tp-select catalog-filter-select"
                  value={query.programGenreId ?? 'all'}
                  onChange={e => {
                    const val = e.target.value
                    setProgramQuery({ programGenreId: val === 'all' ? undefined : val })
                  }}
                  aria-label="Filtrer par genre"
                >
                  <option value="all">Tous genres</option>
                  {genresList.map(g => (
                    <option key={g.id} value={g.id}>{g.label}</option>
                  ))}
                </select>
              </div>

              {/* Collection */}
              <div className="catalog-filter-popover__group">
                <label className="catalog-filter-popover__label">Collection</label>
                <select
                  className="tp-select catalog-filter-select"
                  value={query.collectionId ?? 'all'}
                  onChange={e => {
                    const val = e.target.value
                    setProgramQuery({ collectionId: val === 'all' ? undefined : val, seriesId: undefined })
                  }}
                  aria-label="Filtrer par collection"
                >
                  <option value="all">Toutes collections</option>
                  {collectionsList.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Série */}
              <div className="catalog-filter-popover__group">
                <label className="catalog-filter-popover__label">Série</label>
                <select
                  className="tp-select catalog-filter-select"
                  value={query.seriesId ?? 'all'}
                  onChange={e => {
                    const val = e.target.value
                    setProgramQuery({ seriesId: val === 'all' ? undefined : val, collectionId: undefined })
                  }}
                  aria-label="Filtrer par série"
                >
                  <option value="all">Toutes séries</option>
                  {seriesList.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tri */}
              <div className="catalog-filter-popover__group">
                <label className="catalog-filter-popover__label">Tri des programmes</label>
                <select
                  className="tp-select catalog-filter-select"
                  value={`${query.sortBy ?? 'premiereDate'}:${query.sortDir ?? 'desc'}`}
                  onChange={e => {
                    const [sortBy, sortDir] = e.target.value.split(':')
                    setProgramQuery({ sortBy: sortBy as any, sortDir: sortDir as any })
                  }}
                  aria-label="Trier les programmes"
                >
                  <option value="premiereDate:desc">1re diffusion (plus récente)</option>
                  <option value="premiereDate:asc">1re diffusion (plus ancienne)</option>
                  <option value="createdAt:desc">Récents d'abord</option>
                  <option value="title:asc">Titre (A-Z)</option>
                  <option value="title:desc">Titre (Z-A)</option>
                  <option value="durationSec:desc">Durée (plus long)</option>
                  <option value="durationSec:asc">Durée (plus court)</option>
                </select>
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearAllFilters}
                  className="tp-button catalog-clear-filters-btn"
                >
                  × Réinitialiser tous les filtres
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* ── Badges/Tags des filtres actifs sous la barre ───────────────────── */}
      {!compact && hasActiveFilters && (
        <div className="catalog-active-filter-tags">
          {query.programCategoryId && (
            <span className="catalog-filter-tag">
              Format: {categoriesList.find(c => c.id === query.programCategoryId)?.label ?? 'Actif'}
              <button type="button" onClick={() => setProgramQuery({ programCategoryId: undefined })} title="Retirer ce filtre">×</button>
            </span>
          )}
          {query.programGenreId && (
            <span className="catalog-filter-tag">
              Genre: {genresList.find(g => g.id === query.programGenreId)?.label ?? 'Actif'}
              <button type="button" onClick={() => setProgramQuery({ programGenreId: undefined })} title="Retirer ce filtre">×</button>
            </span>
          )}
          {query.isLive === 'true' && (
            <span className="catalog-filter-tag">
              Directs
              <button type="button" onClick={() => setProgramQuery({ isLive: undefined })} title="Retirer ce filtre">×</button>
            </span>
          )}
          {query.archived === 'archived' && (
            <span className="catalog-filter-tag">
              Archivés
              <button type="button" onClick={() => setProgramQuery({ archived: undefined })} title="Retirer ce filtre">×</button>
            </span>
          )}
          {query.collectionId && (
            <span className="catalog-filter-tag">
              Collection: {collectionsList.find(c => c.id === query.collectionId)?.name ?? 'Active'}
              <button type="button" onClick={() => setProgramQuery({ collectionId: undefined })} title="Retirer ce filtre">×</button>
            </span>
          )}
          {query.seriesId && (
            <span className="catalog-filter-tag">
              Série: {seriesList.find(s => s.id === query.seriesId)?.title ?? 'Active'}
              <button type="button" onClick={() => setProgramQuery({ seriesId: undefined })} title="Retirer ce filtre">×</button>
            </span>
          )}
          {query.search && (
            <span className="catalog-filter-tag">
              Recherche: « {query.search} »
              <button type="button" onClick={() => setProgramQuery({ search: undefined })} title="Effacer la recherche">×</button>
            </span>
          )}
          <button type="button" onClick={handleClearAllFilters} className="catalog-clear-all-link">
            Effacer tout
          </button>
        </div>
      )}

      {/* ── Bannière filtre actif ──────────────────────────────────────────── */}
      {isFiltered && activeFilter && (
        <div className="catalog-active-filter">
          <span className="catalog-active-filter__dot" style={filterStyle} aria-hidden="true" />
          <div className="catalog-active-filter__copy">
            <div className="catalog-active-filter__kind">
              {activeFilter.type === 'collection' ? 'Collection' : 'Série'}
            </div>
            <div className="catalog-active-filter__name">
              {activeFilter.name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClearFilter}
            title="Voir tous les programmes"
            className="tp-button"
          >
            Voir tout
          </button>
        </div>
      )}

      {/* ── Compteur ──────────────────────────────────────────────────────── */}
      <div className="catalog-count">
        <Checkbox
          className="catalog-selection-checkbox"
          checked={allSelected ? true : someSelected ? 'indeterminate' : false}
          disabled={loading || programs.length === 0}
          onClick={handleCheckAll}
          aria-label="Sélectionner tous les programmes affichés"
        />
        <span role="status">
          {loading
            ? <><Spinner /> Chargement…</>
            : `${selectedIds.size > 0 ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? 's' : ''} · ` : ''}${programs.length} programme${programs.length > 1 ? 's' : ''}${hasMore ? ` sur ${total}` : ''}`}
        </span>
        {selectedIds.size > 0 && (
          <>
            {channelId && (
              <ProgramBulkActions
                channelId={channelId}
                selectedIds={[...selectedIds]}
                canDelete={canDelete}
                allowSeriesChange
                onDone={() => {
                  setSelectedIds(new Set())
                  selectionAnchorRef.current = null
                  void reload()
                }}
              />
            )}
            <Button
              type="button"
              variant="destructive"
              size="icon-xs"
              className="catalog-bulk-remove-button"
              onClick={() => void handleDeleteSelected()}
              disabled={deleting || !canDelete}
              aria-label={`Supprimer ${selectedIds.size} programme${selectedIds.size > 1 ? 's' : ''} sélectionné${selectedIds.size > 1 ? 's' : ''}`}
              title={!canDelete ? 'Permission de suppression des programmes requise' : 'Supprimer les programmes sélectionnés'}
            >
              <Minus />
            </Button>
          </>
        )}
      </div>

      {/* ── Liste ─────────────────────────────────────────────────────────── */}
      <div className="catalog-scroll" ref={scrollRef}>
        {programs.map(p => (
          <ProgramRow
            key={p.id}
            program={p}
            active={selected?.id === p.id}
            checked={selectedIds.has(p.id)}
            onSelect={handleSelect}
            onCheck={handleCheck}
          />
        ))}
        {!loading && programs.length === 0 && (
          <div className="catalog-empty">
            Aucun programme trouvé
          </div>
        )}
        {hasMore && (
          <div className="catalog-load-more" ref={sentinelRef}>
            <button
              type="button"
              className="tp-button tp-button--secondary"
              onClick={() => void loadMore()}
              disabled={loadingMore || loading}
            >
              {loadingMore ? <><Spinner /> Chargement…</> : `Charger la suite (${Math.max(0, total - programs.length)})`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
})

// ─── Ligne de programme ───────────────────────────────────────────────────────

/**
 * Date de première diffusion, lue comme le tri du catalogue la lit : la date
 * prévue quand elle est connue, sinon la première diffusion constatée. La liste
 * étant rangée dessus par défaut, la ligne doit porter cette date, sinon
 * l'ordre affiché ne se lit nulle part.
 */
function premiereLabel(program: Program): string | null {
  const iso = program.plannedFirstAirDate ?? program.firstAiredAt
  if (!iso) return null
  const label = new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
  return `1re diff. ${label}`
}

const ProgramRow = memo(function ProgramRow({
  program, active, checked, onSelect, onCheck,
}: {
  program: Program
  active: boolean
  checked: boolean
  onSelect: (p: Program) => void
  onCheck: (p: Program, checked: boolean, shiftKey: boolean) => void
}) {
  const allParts = [program, ...(program.parts ?? [])]
  const hasError = !program.isLive && allParts.some(part => !part.mediaAssets?.length || part.mediaAssets.some(m => ['MISSING', 'INVALID_CODEC'].includes(m.status)))
  // Rappel discret d'une fiche incomplète pour l'EPG : titre court ou résumé vide.
  const missingEpgInfo = !program.titleShort?.trim() || !program.summary?.trim()
  const totalDurationSec = program.blockDurationSec
    ?? allParts.reduce((total, part) => total + part.durationSec, 0) + program.interPartBreakSec * Math.max(0, allParts.length - 1)
  const typeLabel = program.programCategory?.label ?? PROGRAM_TYPE_LABELS[program.type]
  const premiere = premiereLabel(program)
  // La couleur du type ne s'affiche que si rien de plus urgent n'occupe le badge :
  // un direct ou un média manquant priment sur la couleur éditoriale.
  const accentColor = !program.isLive && !hasError ? program.programCategory?.color : null

  return (
    <div className={`catalog-program-row${checked ? ' is-selected' : ''}${active ? ' is-active' : ''}`}>
      <Checkbox
        className="catalog-selection-checkbox"
        checked={checked}
        onClick={event => {
          event.stopPropagation()
          onCheck(program, !checked, event.shiftKey)
        }}
        aria-label={`Sélectionner ${program.title}`}
      />
      <button
        type="button"
        className="catalog-program-row__select"
        aria-current={active ? 'true' : undefined}
        onClick={() => onSelect(program)}
      >
        <span
          className={`catalog-type-badge${program.isLive ? ' catalog-type-badge--live' : hasError ? ' catalog-type-badge--error' : accentColor ? ' catalog-type-badge--accent' : ''}`}
          style={accentColor ? { '--catalog-accent': accentColor } as CSSProperties : undefined}
          title={typeLabel}
        >
          {program.isLive ? 'LIVE' : typeLabel.slice(0, 4).toUpperCase()}
        </span>

        <span className="catalog-program-row__copy">
          <span className="catalog-program-row__title">
            <span className="catalog-program-row__title-text">{program.title}</span>
            {missingEpgInfo && (
              <TriangleAlert
                className="catalog-program-row__epg-flag"
                size={12}
                aria-label="Infos EPG incomplètes (titre court ou résumé manquant)"
              />
            )}
          </span>
          <span className="catalog-program-row__meta">
            {program.programCode ? `${program.programCode} · ` : ''}{program.isLive ? 'Direct · ' : ''}{typeLabel} · {formatDurationShort(totalDurationSec)}
            {allParts.length > 1 && ` · ${allParts.length} parties`}
            {premiere && ` · ${premiere}`}
            {program.isLive && program.liveSource && ` · ${program.liveSource.name}`}
          </span>
        </span>

        <span className={`catalog-status-badge ${program.isLive ? 'catalog-status-badge--warning' : hasError ? 'catalog-status-badge--danger' : 'catalog-status-badge--success'}`}>
          {program.isLive ? 'DIRECT' : hasError ? 'ALERTE' : 'OK'}
        </span>
      </button>
    </div>
  )
})
